// ─────────────────────────────────────────────────────────────────
// Minimal RFC 4180 CSV reader
//
// Handles the things real-world exports throw at us: quoted fields,
// escaped quotes (""), embedded commas and newlines, CRLF/LF/CR row
// terminators, a leading UTF-8 BOM, and semicolon/tab delimited files
// (Excel in European locales, or a copy-paste out of Sheets).
//
// The delimiter is sniffed from the first line unless the caller pins
// one. Nothing here is subscriber-specific — CSV importers for other
// resources can reuse it.
// ─────────────────────────────────────────────────────────────────

/** Delimiters we will sniff for, in preference order on a tie. */
export const CSV_DELIMITERS = [',', ';', '\t', '|'] as const;
export type CsvDelimiter = (typeof CSV_DELIMITERS)[number];

/** A parsed CSV: the cells, the delimiter used, and the record count. */
export interface CsvTable {
  delimiter: CsvDelimiter;
  /** Every record as an array of trimmed cells. Empty records are dropped. */
  rows: string[][];
}

/** True when a record has no non-empty cell. */
function isBlankRecord(cells: string[]): boolean {
  return cells.every((cell) => cell.trim() === '');
}

/**
 * Count delimiter candidates in the first line, ignoring any that appear
 * inside quotes so a single-column file of `"Lagos, Nigeria"` is not
 * mistaken for a comma-delimited file.
 */
function sniffDelimiter(text: string): CsvDelimiter {
  const counts = new Map<CsvDelimiter, number>(CSV_DELIMITERS.map((d) => [d, 0]));
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i]!;
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') i += 1;
        else inQuotes = false;
      }
      continue;
    }
    if (char === '"') {
      inQuotes = true;
      continue;
    }
    if (char === '\n' || char === '\r') break;
    const known = CSV_DELIMITERS.find((d) => d === char);
    if (known) counts.set(known, (counts.get(known) ?? 0) + 1);
  }

  let best: CsvDelimiter = ',';
  let bestCount = 0;
  for (const delimiter of CSV_DELIMITERS) {
    const count = counts.get(delimiter) ?? 0;
    if (count > bestCount) {
      best = delimiter;
      bestCount = count;
    }
  }
  return best;
}

/**
 * Split CSV text into records of cells. Cells keep their inner whitespace
 * (only the outer edges are trimmed) so a quoted `"  Ada  "` survives.
 */
export function parseCsv(text: string, delimiter?: CsvDelimiter): CsvTable {
  // A BOM would otherwise become part of the first header cell, which
  // breaks column matching for files exported by Excel.
  const input = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const sep = delimiter ?? sniffDelimiter(input);

  const rows: string[][] = [];
  let cells: string[] = [];
  let field = '';
  let quoted = false;

  const endField = () => {
    cells.push(quoted ? field : field.trim());
    field = '';
    quoted = false;
  };
  const endRecord = () => {
    endField();
    if (!isBlankRecord(cells)) rows.push(cells);
    cells = [];
  };

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i]!;

    if (quoted) {
      if (char === '"') {
        // A doubled quote inside a quoted field is a literal quote.
        if (input[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    // An opening quote only starts a quoted field at the start of a cell.
    if (char === '"' && field.trim() === '') {
      quoted = true;
      field = '';
      continue;
    }

    if (char === sep) {
      endField();
      continue;
    }

    if (char === '\r' || char === '\n') {
      // CRLF and bare LF/CR all end a record.
      if (char === '\r' && input[i + 1] === '\n') i += 1;
      endRecord();
      continue;
    }

    field += char;
  }

  // A file that does not end in a newline still has a final record.
  if (field !== '' || cells.length > 0) endRecord();

  return { delimiter: sep, rows };
}

/**
 * Normalise a header cell for alias matching: lower-case, strip
 * punctuation, collapse whitespace. "Subscriber_ID" and "subscriber id"
 * both become "subscriber id".
 */
export function normalizeHeaderCell(cell: string): string {
  return cell
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}
